import { retry, RetryConfig, RetryError } from "ts-retry-promise";

type ExtendedRetryConfig = Partial<
  RetryConfig<unknown> & { attemptTimeout: () => number } & { throwOnlyLastError: boolean }
>;
export async function extendedRetry<T>(workFunction: () => Promise<T>, config: ExtendedRetryConfig = {}) {
  const { attemptTimeout, throwOnlyLastError = true, ...configCore } = config;
  try {
    if (attemptTimeout) {
      if (!configCore.timeout) {
        configCore["timeout"] = "INFINITELY";
      }
      return await retry(async () => {
        return await withTimeout(attemptTimeout(), workFunction);
      }, configCore);
    } else {
      return await retry(workFunction, configCore);
    }
  } catch (retryError) {
    if (throwOnlyLastError && retryError instanceof RetryError) {
      throw retryError.lastError;
    }
    throw retryError;
  }
}

type RetryTimoutsConfig = Omit<Partial<RetryConfig<unknown>>, "retries" | "logger" | "timeout">;
export async function retryTimeouts<T>(
  timeouts: number[],
  workFunction: () => Promise<T>,
  config: RetryTimoutsConfig = {},
  errorFunction?: (msg: string, attempt: number) => Promise<void>
) {
  let attempt = 0;

  await retry(
    async () => {
      // Determine the timeout for this attempt
      let timeout = 0;
      if (timeouts?.[attempt]) {
        timeout = timeouts[attempt];
      }
      attempt++; // Increment attempt for next retry
      return await withTimeout(timeout, workFunction);
    },
    {
      retries: timeouts.length - 1,
      timeout: "INFINITELY",
      logger: (msg) => {
        if (errorFunction) {
          errorFunction(msg, attempt);
        }
      },
      ...config,
    }
  );
}

function withTimeout<T>(ms: number, fn: () => Promise<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error(`Function exceeded timeout of ${ms}ms`));
    }, ms);

    fn()
      .then((result) => {
        clearTimeout(timer);
        resolve(result);
      })
      .catch((err) => {
        clearTimeout(timer);
        reject(err);
      });
  });
}
